import { useMemo } from 'react';

import { useQuery } from 'react-query';
import { GET_ADMIN_QR_RISK_LOG_QUERY } from '@app/constants/query-api-configs';

import { useBasicQueryControls } from '@app/hooks/useBasicQueryControls.hook';

import { Typography } from '@mui/material';

import { Button, Table } from '@app/components';
import { useTableColumns } from './useTableColumns';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { EAccountRiskCategory, EQrCodeSourceAction, EWebRiskVerdict } from '@app/swagger-types';
import { oneOfEnum } from '@app/utils/yup.utils';
import { QrCodeRiskLogApi } from '@app/domain/qr-code-risk-log/api/QrCodeRiskLogApi';

export const AdminQrRiskLogTable: React.VFC = ({}) => {
  const {
    values: { page, sort = GET_ADMIN_QR_RISK_LOG_QUERY.defaultSort, size },
    handlers: { handlePageChange, handleSortChange, handleSizeChange },
  } = useBasicQueryControls({
    initialValues: {
      page: 1,
      size: 10,
    },
    prefix: GET_ADMIN_QR_RISK_LOG_QUERY.tablePrefix,
  });
  const [qrCodeId] = useQueryParam(GET_ADMIN_QR_RISK_LOG_QUERY.qrCodeIdParam, withDefault(StringParam, ''));
  const [wouldBlock] = useQueryParam(GET_ADMIN_QR_RISK_LOG_QUERY.wouldBlockParam, withDefault(StringParam, ''));
  const [accountCategory] = useQueryParam(
    GET_ADMIN_QR_RISK_LOG_QUERY.accountCategoryParam,
    withDefault(StringParam, '')
  );
  const [sourceAction] = useQueryParam(GET_ADMIN_QR_RISK_LOG_QUERY.sourceActionParam, withDefault(StringParam, ''));
  const [webRiskVerdict] = useQueryParam(GET_ADMIN_QR_RISK_LOG_QUERY.webRiskVerdictParam, withDefault(StringParam, ''));
  const [reviewed] = useQueryParam(GET_ADMIN_QR_RISK_LOG_QUERY.reviewedParam, withDefault(StringParam, ''));

  const { data, isFetching, refetch } = useQuery(
    [
      GET_ADMIN_QR_RISK_LOG_QUERY.name,
      { page, sort, size, qrCodeId, wouldBlock, accountCategory, sourceAction, webRiskVerdict, reviewed },
    ],
    () => {
      const validAccountCategory = oneOfEnum(EAccountRiskCategory).isValidSync(accountCategory)
        ? accountCategory
        : undefined;
      const validSourceAction = oneOfEnum(EQrCodeSourceAction).isValidSync(sourceAction) ? sourceAction : undefined;
      const validWebRiskVerdict = oneOfEnum(EWebRiskVerdict).isValidSync(webRiskVerdict) ? webRiskVerdict : undefined;
      return QrCodeRiskLogApi.adminGetAll({
        sort,
        page: Math.ceil((page || 1) - 1),
        size,
        qrCodeId: qrCodeId || undefined,
        wouldBlock: wouldBlock === 'true' ? true : wouldBlock === 'false' ? false : undefined,
        accountCategory: validAccountCategory,
        sourceAction: validSourceAction,
        webRiskVerdict: validWebRiskVerdict,
        reviewed: reviewed === 'true' ? true : reviewed === 'false' ? false : undefined,
      });
    },
    { keepPreviousData: true, ...GET_ADMIN_QR_RISK_LOG_QUERY.config }
  );

  const columns = useTableColumns();

  const leftContent = useMemo(() => {
    return (
      <div>
        <Typography variant="xl" className="font-bold">
          QR risk log ({data?.total})
        </Typography>
      </div>
    );
  }, [data?.total]);

  const rightContent = useMemo(() => {
    return (
      <div>
        <Button loading={isFetching} onClick={() => refetch()}>
          Refetch
        </Button>
      </div>
    );
  }, [refetch, isFetching]);

  return (
    <Table
      cols={columns}
      tableData={data?.result ?? []}
      leftHeaderContent={leftContent}
      rightHeaderContent={rightContent}
      notFoundMessage="No flagged QR codes yet."
      currentPage={page}
      loading={isFetching}
      pageSize={size}
      onSizeChange={handleSizeChange}
      total={data?.total}
      onPagination={handlePageChange}
      currentSort={sort}
      onSortChanged={handleSortChange}
    />
  );
};
